import { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import styles from './style.module.scss'

/**
 * Vergrößerte Bildansicht mit Blättern.
 *
 * Tastatur ist hier kein Beiwerk: Wer mehrere Screenshots eines Fehlers
 * durchsieht, tut das mit den Pfeiltasten, nicht mit der Maus. Links, rechts
 * und Escape sind belegt.
 *
 * Während die Ansicht offen ist, wird das Scrollen der Seite dahinter
 * unterbunden — sonst wandert die Liste im Hintergrund weg und man findet
 * nach dem Schließen seine Stelle nicht wieder.
 */
export default function Lightbox(props) {
	const { index, images, onClose, onIndexChange } = props
	const total = images.length

	const step = useCallback(
		delta => {
			if (total < 2) return
			// Umlaufend: Nach dem letzten kommt wieder das erste. Bei zwei, drei
			// Bildern ist das angenehmer als ein toter Knopf am Rand.
			onIndexChange((index + delta + total) % total)
		},
		[index, total, onIndexChange]
	)

	useEffect(() => {
		const onKey = event => {
			if (event.key === 'Escape') onClose()
			if (event.key === 'ArrowRight') step(1)
			if (event.key === 'ArrowLeft') step(-1)
		}

		document.addEventListener('keydown', onKey)
		const previousOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'

		return () => {
			document.removeEventListener('keydown', onKey)
			document.body.style.overflow = previousOverflow
		}
	}, [onClose, step])

	const current = images[index]
	if (!current) return null

	return (
		<div
			className={styles.backdrop}
			role="dialog"
			aria-modal="true"
			aria-label="Vergrößerte Ansicht"
			// Klick auf den Hintergrund schliesst. Klicks aus dem Inhalt heraus
			// steigen bis hierher auf - deshalb die Pruefung auf das Ziel.
			onClick={event => {
				if (event.target === event.currentTarget) onClose()
			}}
		>
			<button type="button" className={styles.close} onClick={onClose} aria-label="Schließen">
				×
			</button>

			{total > 1 && (
				<button
					type="button"
					className={`${styles.nav} ${styles.prev}`}
					onClick={() => step(-1)}
					aria-label="Vorheriges Bild"
				>
					‹
				</button>
			)}

			<figure className={styles.figure}>
				<img className={styles.image} src={current.src} alt={current.caption || 'Screenshot'} />
				<figcaption className={styles.caption}>
					{current.caption && <span className={styles.captionText}>{current.caption}</span>}
					{total > 1 && (
						<span className={styles.counter}>
							{index + 1} von {total}
						</span>
					)}
				</figcaption>
			</figure>

			{total > 1 && (
				<button
					type="button"
					className={`${styles.nav} ${styles.next}`}
					onClick={() => step(1)}
					aria-label="Nächstes Bild"
				>
					›
				</button>
			)}
		</div>
	)
}

Lightbox.propTypes = {
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			caption: PropTypes.string
		})
	).isRequired,
	index: PropTypes.number.isRequired,
	onIndexChange: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired
}
